import React, { useState, useRef, useCallback, useEffect } from "react";
import { Overlay, Popover, Button } from "react-bootstrap";
import { BsBellFill } from "react-icons/bs";
import NotificationList from "./NotificationList";
import { useAuth } from "@/context/auth-context";
import { useNotificationSignalR } from "@/hooks/useNotificationSignalR";
import { useSignalRReconnect } from "@/hooks/useSignalRReconnect";
import { useNotifications } from "@/hooks/useNotifications";
import { getConnectionStatus } from "@/services/notificationSignalR";
import { Notification } from "@/services/notificationService";
import logger from "@/utils/logger";

const NotificationPopover: React.FC = () => {
    logger.info("🔔 [NotificationPopover] Component rendering/mounting");

    const [show, setShow] = useState(false);
    const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
    const [highlightId, setHighlightId] = useState<string | null>(null);
    const target = useRef(null);
    const { userProfile, msalInstance } = useAuth();
    const { notifications, refetch, addNotification } = useNotifications(
        "Dispatch IQ",
        userProfile?.email || "",
        msalInstance
    );

    // Check for unread notifications whenever notifications change
    useEffect(() => {
        const hasUnread = notifications.some(
            (notification) => !notification.readIndicator
        );
        setHasUnreadNotifications(hasUnread);
    }, [notifications]);

    // Monitor SignalR connection status (for debugging purposes)
    useEffect(() => {
        const checkConnectionStatus = () => {
            const status = getConnectionStatus();
            // Connection status is checked but not stored in state to avoid unused variable
            if (process.env.NODE_ENV === "development") {
                logger.info("SignalR status:", status);
            }
        };

        // Check immediately
        checkConnectionStatus();

        // Check periodically (every 30 seconds)
        const interval = setInterval(checkConnectionStatus, 30000);

        return () => clearInterval(interval);
    }, []);

    logger.info(
        "🔔 [NotificationPopover] About to register SignalR handler, userProfile:",
        userProfile?.email
    );

    // SignalR: Listen for new notifications
    useNotificationSignalR(
        "Dispatch IQ",
        userProfile?.email || "",
        useCallback(
            (notification: unknown) => {
                logger.info(
                    "🚀 [DispatchNotificationToast] Handler triggered!"
                );
                // When a new notification arrives, add it immediately to local state
                if (notification && typeof notification === "object") {
                    // Transform the SignalR notification to match the Notification type
                    const newNotification = {
                        ...notification,
                        readIndicator: false, // New notifications are always unread
                        createdOn: new Date().toISOString(),
                    } as Notification; // Type assertion to Notification interface

                    // Add to local state immediately for instant UI update
                    addNotification(newNotification);

                    // Set unread indicator immediately
                    setHasUnreadNotifications(true);

                    if ("id" in notification) {
                        setHighlightId((notification as { id: string }).id);
                    }

                    // Refetch after a delay to sync with backend
                    setTimeout(() => {
                        refetch();
                    }, 1000);
                }
            },
            [refetch, addNotification]
        ),
        undefined, // No trip status change handler needed for notifications
        "DispatchNotificationToast" // Descriptive handler name
    );

    useSignalRReconnect(
        useCallback(() => {
            logger.info(
                "🔔 [NotificationPopover] SignalR reconnected, refetching notifications"
            );
            void refetch();
        }, [refetch]),
        "NotificationPopoverReconnectRecovery"
    );

    // Handle popover visibility and refetch after closing
    const handleShow = () => {
        setShow((prev) => !prev);
        if (!show) {
            // Opening popover - clear highlight
            setHighlightId(null);
        } else {
            // Closing popover - refetch after a short delay to update unread status
            setTimeout(() => {
                refetch();
            }, 500); // 500ms delay to allow marking as read to complete
        }
    };

    // Handle popover close from overlay
    const handleHide = () => {
        setShow(false);
        // Refetch after closing to update unread status
        setTimeout(() => {
            refetch();
        }, 500);
    };

    return (
        <>
            <Button
                variant="link"
                ref={target}
                onClick={handleShow}
                style={{ boxShadow: "none" }}
                className={`p-0 me-3 position-relative notification-bell-btn secondary-color-text ${hasUnreadNotifications ? "notification-indicator" : ""}`}
                aria-label="Notifications"
            >
                <BsBellFill size={26} className="secondary-color-text" />
                {hasUnreadNotifications && (
                    <span className="notification-indicator-primary" />
                )}
            </Button>
            <Overlay
                show={show}
                target={target.current}
                placement="bottom-end"
                containerPadding={20}
                rootClose
                onHide={handleHide}
                popperConfig={{
                    modifiers: [
                        {
                            name: "offset",
                            options: {
                                offset: [30, 8],
                            },
                        },
                        {
                            name: "preventOverflow",
                            options: {
                                boundary: "viewport",
                                padding: 20,
                            },
                        },
                        {
                            name: "arrow",
                            options: {
                                padding: 5,
                            },
                        },
                        {
                            name: "flip",
                            enabled: false,
                        },
                    ],
                }}
            >
                <Popover
                    id="notification-popover"
                    style={{ minWidth: 350, maxWidth: 400 }}
                    className="notification-popover-responsive"
                >
                    <Popover.Body className="p-0">
                        <NotificationList
                            highlightId={highlightId}
                            applicationName="Dispatch IQ"
                            userEmail={userProfile?.email || ""}
                            msalInstance={msalInstance}
                            onClose={handleHide}
                        />
                    </Popover.Body>
                </Popover>
            </Overlay>

            <style jsx global>{`
                .notification-bell-btn {
                    color: #000 !important;
                    transition: background 0.2s;
                }
                .notification-bell-btn:hover {
                    background: rgba(255, 255, 255, 0.15) !important;
                }

                /* Notification indicator styles */
                .notification-indicator {
                    position: relative;
                }

                .notification-indicator:before {
                    background-color: #ffb340 !important;
                    border-color: #ffb340 !important;
                }

                .notification-indicator:after {
                    position: absolute;
                    content: "";
                    right: 0.05rem;
                    top: 0.1rem;
                    height: 0.5rem;
                    width: 0.5rem;
                    border-radius: 50%;
                    background-color: #ffa200;
                    animation: pulse 1.5s infinite;
                }

                .notification-indicator-primary:before {
                    background-color: #ffb340 !important;
                    border-color: #ffb340 !important;
                }

                .notification-indicator-primary {
                    position: absolute;
                    right: 0.05rem;
                    top: 0.1rem;
                    height: 0.5rem;
                    width: 0.5rem;
                    border-radius: 50%;
                    background-color: #ffa200;
                    animation: pulse 1.5s infinite;
                }

                @keyframes pulse {
                    0% {
                        opacity: 1;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.5;
                        transform: scale(1.5);
                    }
                    100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                }

                /* Arrow positioning to point at bell icon */
                .notification-popover-responsive .popover-arrow {
                    left: auto !important;
                    right: 42px !important;
                    transform: translateX(0) !important;
                }

                /* Arrow positioning for full-screen/desktop */
                @media (min-width: 1200px) {
                    .notification-popover-responsive .popover-arrow {
                        right: 40px !important;
                    }
                }

                /* Mobile responsive full-screen - match desktop appearance */
                @media (max-width: 768px) {
                    div[data-popper-placement] {
                        position: fixed !important;
                        inset: 0 !important;
                        transform: none !important;
                        z-index: 9999 !important;
                    }

                    .notification-popover-responsive {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        right: 0 !important;
                        bottom: 0 !important;
                        min-width: 100vw !important;
                        max-width: 100vw !important;
                        width: 100vw !important;
                        height: 100vh !important;
                        margin: 0 !important;
                        border-radius: 0 !important;
                        border: none !important;
                    }

                    .notification-popover-responsive .popover-arrow {
                        display: none !important;
                    }

                    .notification-popover-responsive .popover-body {
                        height: 100vh !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                }
            `}</style>
        </>
    );
};

export default NotificationPopover;
